import { selectedFeatureAtom } from '@/atoms/selection.atom';
import {
  addLineModeAtom,
  DEFAULT_ADD_LINE_MODE,
  type LinePart,
} from '@/atoms/viewer.atom';
import { project } from '@/core';
import { execute } from '@/graphql/execute';
import { useFeatureActions } from '@/hooks/useFeatureActions';
import { formatLength, polylineLength } from '@/lib/geometryMeasure';
import { createFeatureMutation, updateFeatureMutation } from '@/queries/feature.query';
import {
  Check,
  CheckSquare,
  Plus,
  Trash,
  Undo,
} from '@mynaui/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const isTypingTarget = (e: KeyboardEvent) => {
  const t = e.target as HTMLElement | null;
  if (!t) return false;
  return (
    t.tagName === 'INPUT' ||
    t.tagName === 'TEXTAREA' ||
    t.tagName === 'SELECT' ||
    t.isContentEditable
  );
};

import {
  FloatingToolbar,
  FloatingToolbarButton,
  FloatingToolbarSeparator,
} from './FloatingToolbar';

interface AddLineToolbarProps {
  srid: number;
}

const LINE_FEATURE_TYPE = 'line';
const MIN_VERTICES_PER_PART = 2;

export const AddLineToolbar = ({ srid }: AddLineToolbarProps) => {
  const [mode, setMode] = useAtom(addLineModeAtom);
  const selectedFeature = useAtomValue(selectedFeatureAtom);
  const setSelectedFeature = useSetAtom(selectedFeatureAtom);
  const queryClient = useQueryClient();
  const { selectFeatureForEditing, createUpdateHandler } = useFeatureActions();
  const { t } = useTranslation('components');

  const isPicking = mode.phase === 'picking';
  const totalParts = mode.parts.length + (mode.currentPart.length >= MIN_VERTICES_PER_PART ? 1 : 0);
  const currentValid = mode.currentPart.length >= MIN_VERTICES_PER_PART;
  // Either: at least one previously-committed part exists, OR the current
  // part is valid. Otherwise there is nothing to finalize.
  const canFinalize = isPicking && (mode.parts.length > 0 || currentValid);
  // Same logic as AddPolygonToolbar: enabled when there's a valid
  // current part to commit OR when at least one part is already
  // committed and current is empty (state right after a snap-to-first
  // close — clicking the button is a no-op data-wise but signals the
  // multi-line flow is alive). Without this the button stayed grey
  // after closing a line loop and the user couldn't tell they were
  // free to start the next part.
  const canAddPart =
    isPicking && (currentValid || (mode.parts.length > 0 && mode.currentPart.length === 0));
  const canUndo = isPicking && (mode.currentPart.length > 0 || mode.parts.length > 0);

  // Live total length across every committed part plus the in-progress one.
  // Cheap (sum of distances) so we recompute every render — the toolbar
  // already re-renders on every pick anyway.
  const totalLengthM =
    mode.parts.reduce((sum, p) => sum + polylineLength(p), 0) + polylineLength(mode.currentPart);
  const lengthLabel = totalLengthM > 0 ? ` · ${formatLength(totalLengthM)}` : '';

  const status =
    (!isPicking
      ? t('addLineTool.adjusting')
      : mode.currentPart.length === 0
        ? mode.parts.length === 0
          ? t('addLineTool.pickFirstPoint')
          : t('addLineTool.pickFirstPointOfPart', { partIndex: mode.parts.length + 1 })
        : t('addLineTool.pickNextOrFinalize', {
            count: mode.currentPart.length,
            parts: totalParts,
          })) + lengthLabel;

  const reset = () => setMode({ ...DEFAULT_ADD_LINE_MODE });

  /**
   * Pop the most recent vertex. If the current part is empty but there are
   * previously-committed parts, restore the last committed part as the
   * current part (so undo never gets stuck with no way to remove the last
   * part's vertices). Restored as-is — no closing duplicate was added at
   * "+ Part" time, so the part is already in open form.
   */
  const handleUndo = () => {
    if (!isPicking) return;
    if (mode.currentPart.length > 0) {
      setMode((prev) => ({ ...prev, currentPart: prev.currentPart.slice(0, -1) }));
      return;
    }
    if (mode.parts.length > 0) {
      const restored = mode.parts[mode.parts.length - 1];
      setMode((prev) => ({
        ...prev,
        parts: prev.parts.slice(0, -1),
        currentPart: restored,
      }));
    }
  };

  const handleAddPart = () => {
    if (!canAddPart) return;
    // Only commit when the current part is actually valid; an empty
    // current part means the user just closed a loop via snap-to-first
    // and the button click is a confirming no-op.
    setMode((prev) => {
      if (prev.currentPart.length < MIN_VERTICES_PER_PART) return prev;
      return {
        ...prev,
        parts: [...prev.parts, prev.currentPart],
        currentPart: [],
      };
    });
  };

  /** Build the geometry for persistence: LineString if 1 part, MultiLineString if more. */
  const buildGeometry = (allParts: LinePart[], targetSrid: number) => {
    if (allParts.length === 1) {
      return {
        type: 'LineString' as const,
        crs: {
          type: 'name' as const,
          properties: { name: `urn:ogc:def:crs:EPSG::${targetSrid}` },
        },
        coordinates: allParts[0],
      };
    }
    return {
      type: 'MultiLineString' as const,
      crs: {
        type: 'name' as const,
        properties: { name: `urn:ogc:def:crs:EPSG::${targetSrid}` },
      },
      coordinates: allParts,
    };
  };

  const projectPartsToWGS84 = async (
    allParts: LinePart[],
  ): Promise<LinePart[] | null> => {
    if (!srid || srid === -1) return null;
    try {
      const out: LinePart[] = [];
      for (const part of allParts) {
        const projected = await Promise.all(
          part.map((c) => project(c, `EPSG:${srid}`)),
        );
        if (projected.some((p) => !p)) return null;
        out.push(projected as LinePart);
      }
      return out;
    } catch {
      return null;
    }
  };

  const buildLineProperties = (existing: Record<string, any> = {}) => ({
    ...existing,
    featureType: LINE_FEATURE_TYPE,
    Name: existing.Name ?? `line ${Math.random().toString(36).slice(2, 8)}`,
  });

  /**
   * Collect every part the user has picked, including the in-progress
   * `currentPart` if it has enough vertices. Drops a current part with <2
   * vertices (in-progress) so the user can hit Finalize after a "+ Part"
   * without committing an empty stroke.
   */
  const collectAllParts = (): LinePart[] => {
    if (mode.currentPart.length >= MIN_VERTICES_PER_PART) {
      return [...mode.parts, mode.currentPart];
    }
    return mode.parts;
  };

  const handleFinalize = () => {
    if (!canFinalize) return;
    const parts = collectAllParts();
    if (parts.length === 0) return;
    setMode((prev) => ({
      ...prev,
      parts,
      currentPart: [],
      phase: 'adjusting',
    }));
  };

  const handleCancel = () => {
    if (mode.phase === 'adjusting' && selectedFeature?.isDraft) {
      // Drop draft selectedFeature on cancel.
      setSelectedFeature(null);
    }
    reset();
  };

  const handleOk = async () => {
    const parts = mode.parts;
    if (parts.length === 0 || !mode.targetLayerId) return;
    const geometry = buildGeometry(parts, srid);
    const wgs84Parts = await projectPartsToWGS84(parts);
    const existing = mode.editFeatureId
      ? ((selectedFeature?.properties as Record<string, any> | undefined) ?? {})
      : {};
    const properties = buildLineProperties(existing);

    if (mode.editFeatureId) {
      const toastId = toast.loading(t('addLineTool.saving'));
      try {
        const input: any = { id: mode.editFeatureId, geometry, properties };
        if (wgs84Parts) input.geometryWGS84 = buildGeometry(wgs84Parts, 4326);
        const result = await execute(updateFeatureMutation, { input });
        const updated = result?.updateFeature;
        toast.success(t('addLineTool.saved'), { id: toastId });
        queryClient.invalidateQueries({ queryKey: ['features', mode.targetLayerId] });
        setSelectedFeature((prev) =>
          prev && updated
            ? {
                ...prev,
                geometry: updated.geometry,
                geometryWGS84: updated.geometryWGS84,
                properties: updated.properties,
              }
            : prev,
        );
        reset();
      } catch (e: any) {
        toast.error(e?.message || t('addLineTool.failedSave'), { id: toastId });
      }
      return;
    }

    const toastId = toast.loading(t('addLineTool.creating'));
    try {
      const input: any = { layerId: mode.targetLayerId, geometry, properties };
      if (wgs84Parts) input.geometryWGS84 = buildGeometry(wgs84Parts, 4326);
      const result = await execute(createFeatureMutation, { input });
      if (!result?.createFeature) throw new Error(t('addLineTool.failedCreate'));

      toast.success(t('addLineTool.created'), { id: toastId });
      queryClient.invalidateQueries({ queryKey: ['features', mode.targetLayerId] });

      const newFeature = result.createFeature;
      reset();
      selectFeatureForEditing({
        ...newFeature,
        layerId: mode.targetLayerId,
        onSave: createUpdateHandler(newFeature.id, mode.targetLayerId),
      });
    } catch (e: any) {
      toast.error(e?.message || t('addLineTool.failedCreate'), { id: toastId });
    }
  };

  const isAdjusting = mode.phase === 'adjusting';

  // Keyboard shortcuts: Esc cancels, Enter finalizes (or saves in adjusting),
  // Backspace / Ctrl+Z undoes the last vertex. Re-registers on every mode
  // change so the listener always closes over fresh handlers.
  useEffect(() => {
    if (!mode.enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (isTypingTarget(e)) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (isAdjusting) handleOk();
        else if (canFinalize) handleFinalize();
      } else if (
        e.key === 'Backspace' ||
        (e.key.toLowerCase() === 'z' && (e.ctrlKey || e.metaKey))
      ) {
        if (canUndo) {
          e.preventDefault();
          handleUndo();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // The handlers close over `mode` / `selectedFeature` and are re-created
    // every render; we depend on those upstream values directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selectedFeature, isAdjusting, canFinalize, canUndo]);

  if (!mode.enabled) return null;

  return (
    <FloatingToolbar status={status}>
      <FloatingToolbarButton
        icon={<Undo />}
        tooltip={t('addLineTool.undoPick')}
        disabled={!canUndo}
        onClick={handleUndo}
      />
      <FloatingToolbarButton
        icon={<Plus />}
        tooltip={t('addLineTool.addPart')}
        disabled={!canAddPart}
        onClick={handleAddPart}
      />
      <FloatingToolbarButton
        icon={<CheckSquare />}
        tooltip={t('addLineTool.finalize')}
        disabled={!canFinalize}
        onClick={handleFinalize}
      />
      <FloatingToolbarSeparator />
      <FloatingToolbarButton
        icon={<Check />}
        tooltip={mode.editFeatureId ? t('addLineTool.save') : t('addLineTool.ok')}
        disabled={!isAdjusting}
        intent='primary'
        primaryClass='bg-emerald-600 hover:bg-emerald-700'
        onClick={handleOk}
      />
      <FloatingToolbarButton
        icon={<Trash />}
        tooltip={t('addLineTool.cancel')}
        intent='danger'
        onClick={handleCancel}
      />
    </FloatingToolbar>
  );
};
